<script setup lang="ts">
import { PropType, Ref, onMounted, ref, watch } from 'vue';
import {
  ContainerTypes,
  Helpers,
  initializeNeededServices,
} from '@dandelion/core';
import ColorSelector from './ColorSelector.vue';
import DaysComponent from './DaysComponent.vue';
import { DaysText } from '../enum/DaysText';

const props = defineProps({
    context: {
        type: String,
        required: true,
    },
    fullName: {
        type: String,
        required: true,
    },
    id: {
        type: String,
        required: true,
    },
    containerType: {
      type: String as PropType<ContainerTypes>,
      required: true,
      default: () => ContainerTypes.DEFAULT,
    },
});

let { currentCaseService, model } = initializeNeededServices(props.context, props.containerType as ContainerTypes);

const PROCEDURES_LIST = 'core.proceduresTreatments.proceduresList';
const ADMINISTERED_AMINE = 'start.administeredAmine.aminePlugin';
const procedurePeriods = ref<Array<any>>([]);

const days: Ref<{ [key: string]: string }> = ref({});

const aminePlugin = Helpers.getObjByPath(ADMINISTERED_AMINE, model);

onMounted(() => {
  if (!aminePlugin) {
    Helpers.setObjByPath(ADMINISTERED_AMINE, model, days);
  }

  watch(
    () => Helpers.getObjByPath(PROCEDURES_LIST, model),
    () => {
      buildPeriodsAndRemoveDays();
    },
    { immediate: true, deep: true }
  );
});

function buildPeriodsAndRemoveDays() {
  days.value = Helpers.getObjByPath(ADMINISTERED_AMINE, model);
  const procedureList = Helpers.getObjByPath(PROCEDURES_LIST, model);
  const farmaciVasoattivi = procedureList?.filter(
    (procedure: any) =>
      procedure.procedure == 'procTreatCodification.vasoactDrugs'
  )[0];
  if (!farmaciVasoattivi) {
    procedurePeriods.value = [];
    days.value = {};
  } else {
    procedurePeriods.value = farmaciVasoattivi.procedurePeriods;

    Object.keys(days.value ?? {}).forEach((day) => {
      const isInSomePeriod = procedurePeriods.value.some(
        (period: any) =>
          period.procedureStartDate <= day && period.procedureEndDate >= day
      );
      if (!isInSomePeriod) {
        delete days.value[day];
      }
    });
  }
  Helpers.setObjByPath(ADMINISTERED_AMINE, model, days.value);
}

async function setDay(day: string, newValue: string) {
  const model = currentCaseService.getModel();
  days.value = Helpers.getObjByPath(ADMINISTERED_AMINE, model);

  if (newValue === DaysText.DAY_NOT_COMPILED) {
    delete days.value[day];
    Helpers.setObjByPath(ADMINISTERED_AMINE, model, days);
    return;
  }

  days.value[day] = newValue;
  Helpers.setObjByPath(ADMINISTERED_AMINE, model, days);
}

const currentColor = ref(DaysText.GREY);
const colorButtons = [
  {
    color: DaysText.GREY,
    label: DaysText.VASOACTIVE_DAY_NOT_COMPILED,
  },
  {
    color: DaysText.YELLOW,
    label: DaysText.DAY_WITH_ONE_VASOACTIVE,
  },
  {
    color: DaysText.RED,
    label: DaysText.DAY_WITH_TWO_VASOACTIVE,
  },
];

const setColor = (color: DaysText) => {
  currentColor.value = color;
};
</script>

<template>
  <div id="amineContainer">
    <ColorSelector
      :cb-array="colorButtons"
      @setColor="setColor"
    ></ColorSelector>
    <section id="aminePeriodList">
      <template v-for="item in procedurePeriods">
        <DaysComponent
          :startDate="item.procedureStartDate"
          :endDate="item.procedureEndDate"
          :color="currentColor"
          :dayValues="days"
          @set-day="setDay"
        />
      </template>
    </section>
  </div>
</template>

<style scoped>
#start\.administeredAmine\.aminePlugin {
  grid-column: 1 / 3;
  overflow: hidden;
}

#amineContainer,
#aminePeriodList {
  padding: 0;
}
</style>
